import { useMemo, useState, useCallback, useRef } from 'react';
import { useSlateSelector, useSlateStatic, ReactEditor } from 'slate-react';
import { Editor as SlateEditor, Element, Transforms } from 'slate';
import { TaxonomyChild, TagWithMetadata, useCreateTag, useContinueTag, useConcept, useDocumentTaxonomyConfig, useCreateFootnote } from 'entities/tag';
import {
  useCreateAndInsertTag,
  selectSelectionElementId,
  insertExclude,
  insertFootnote,
  applyBlockTagBinding,
  collectChainCarriers,
  ContinuationTagRef,
  SuccessorTagRef,
} from 'slate/extensions/tag';
import { v4 as uuidv4 } from 'uuid';
import { carrierHoldsAnyTag, listItemsRange, selectionBlockCarrier, BlockCarrier, BlockSelection as CarrierBlockSelection } from './blockCarrier';
import { useTagSelection } from 'app/state/contexts/TagContext';
import { getTagType, isFractionConceptType } from 'app/utils/tag-utils';
import { isEscapedConceptType } from 'app/utils/tag-utils/getConceptNarrowType';
import { findChainInsertionPoint } from './tagChainUtils';
import { useApplyCellTags } from './useApplyCellTags';
import { useApplyBlockChain } from './useApplyBlockChain';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCoarse, useEditorLiveSection } from 'app/state/contexts/EditorUiContext';

interface UseTaggingParams {
  selectedTag: TaxonomyChild | null;
  selectedLinkroleUri?: string | null;
  isContentMarked: boolean;
  documentTags: TagWithMetadata[];
  onCellTagsApplied?: (tagIds: string[]) => void;
}

// Shape of a table-tag selection stashed in TagSelectionContext (typed `any` upstream).
type TableCell = { row: number; col: number; value?: string | number | null; conceptId?: string; linkroleUri?: string | null };
type TableSelection = {
  element?: Element;
  referenceDataSuperId: string;
  cells: TableCell[];
  onTagsCreated?: () => void;
};

// Shape of the draft-tag anchor stashed in TagSelectionContext (typed `any` upstream).
type DraftTagRef = { onId?: string };

export const useTagging = ({ selectedTag, selectedLinkroleUri, isContentMarked, documentTags, onCellTagsApplied }: UseTaggingParams) => {
  const { mutate: createTag } = useCreateTag();
  const { mutate: continueTag } = useContinueTag();
  const createAndInsertTag = useCreateAndInsertTag();
  const activeSection = useEditorCoarse().activeSection as { uuid: string };
  const section = useEditorLiveSection() as { superId: string };
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();
  const { data: conceptData } = useConcept(selectedTag?.id ?? null, pDocument?.superId);
  const { tableSelection: tableSelectionRaw, draftTag: draftTagRaw, blockSelection, blockScope, clearBlockSelection } = useTagSelection();
  const tableSelection = tableSelectionRaw as TableSelection | null;
  const draftTag = draftTagRaw as DraftTagRef | null;
  const { data: config } = useDocumentTaxonomyConfig(pDocument?.superId);
  const taxonomyId = config?.taxonomySelections?.find((s) => s.isActive)?.taxonomy?.id ?? null;
  const editor = useSlateStatic();
  const applyCellTags = useApplyCellTags();
  const applyBlockChain = useApplyBlockChain();
  const { mutate: createFootnote } = useCreateFootnote();

  // Continuation modal state
  const [showContinuationModal, setShowContinuationModal] = useState(false);
  const [pendingBlockChain, setPendingBlockChain] = useState(false);
  const [pendingChainMulti, setPendingChainMulti] = useState<false | 'text' | 'block'>(false);

  const selectionElementId = useSlateSelector(selectSelectionElementId) ?? draftTag?.onId ?? null;

  const isTableTagging = !!tableSelection?.cells?.length;

  const isProseCarrier = blockSelection?.kind === 'paragraph' || blockSelection?.kind === 'list';
  const isTextBlockConcept = isEscapedConceptType(selectedTag?.conceptType);
  const isListItemTagging = isTextBlockConcept && blockSelection?.kind === 'list' && blockScope === 'text';
  const proseScope: 'block' | 'text' = isTextBlockConcept && !isListItemTagging ? 'block' : 'text';
  const effectiveScope = isProseCarrier ? proseScope : blockScope;

  const isImageBlockTagging = blockSelection?.kind === 'image';
  const isProseBlockTagging = isProseCarrier && proseScope === 'block';
  const isTableBlockTagging = !!tableSelection?.element && blockScope === 'table';
  const isBlockTagging = isImageBlockTagging || isProseBlockTagging || isTableBlockTagging;

  // Determine if content is marked (either text selection or table selection)
  const hasContentMarked = isContentMarked || isTableTagging || isBlockTagging;

  // Find existing tags with the same concept in the document
  const existingTagsWithSameConcept = useMemo(() => {
    if (!selectedTag || !documentTags) return [];
    return documentTags.filter((t) => t.concept?.name === selectedTag.id && !t.metadata?.unplaced);
  }, [selectedTag, documentTags]);

  const conceptExistsInDocument = existingTagsWithSameConcept.length > 0;

  const applyingCellsRef = useRef(false);
  const handleTableTagging = async () => {
    if (!tableSelection?.cells?.length || !selectedTag || !conceptData?.id || applyingCellsRef.current) return;
    applyingCellsRef.current = true;
    const { appliedTagIds } = await applyCellTags({
      mode: 'create',
      cells: tableSelection.cells,
      referenceDataSuperId: tableSelection.referenceDataSuperId,
      conceptId: conceptData.id,
      linkroleUri: selectedLinkroleUri ?? null,
      type: getTagType(selectedTag.conceptType.baseXsdType ?? selectedTag.conceptType.baseXbrliType),
      taxonomyId: taxonomyId ?? '',
      sectionContext: {
        documentSuperId: pDocument.superId,
        sectionSuperId: section.superId,
        pageId: activeSection.uuid,
      },
      locale,
      keepSelection: true,
      blockedTagIds: new Set(existingTagsWithSameConcept.map((t) => t.id)),
    }).finally(() => {
      applyingCellsRef.current = false;
    });
    if (appliedTagIds.length > 0) onCellTagsApplied?.(appliedTagIds);
  };

  const createNormalTag = useCallback(() => {
    if (!selectedTag) return;
    const selectedText = editor.selection ? SlateEditor.string(editor, editor.selection) : undefined;

    createAndInsertTag({
      editor,
      tagType: selectedTag.id,
      tagData: {
        taxonomyId: taxonomyId ?? '',
        documentSuperId: pDocument.superId,
        sectionSuperId: section.superId,
        pageId: activeSection.uuid,
        onId: selectionElementId,
        conceptId: conceptData?.id ?? '',
        lang: locale,
        type: getTagType(selectedTag.conceptType.baseXsdType ?? selectedTag.conceptType.baseXbrliType),
        value: selectedText || undefined,
        linkroleUri: selectedLinkroleUri ?? null,
      },
      continuationTag: null,
      callbacks: {},
    });
    clearBlockSelection?.();
  }, [
    selectedTag,
    selectedLinkroleUri,
    editor,
    taxonomyId,
    pDocument,
    section,
    activeSection,
    selectionElementId,
    conceptData,
    locale,
    createAndInsertTag,
    clearBlockSelection,
  ]);

  const createContinuationTag = useCallback(
    (continuationSource: ContinuationTagRef | null, successor?: SuccessorTagRef | null) => {
      if (!selectedTag) return;
      // Must have either a predecessor or a successor to form a continuation
      if (!continuationSource && !successor) return;
      const selectedText = editor.selection ? SlateEditor.string(editor, editor.selection) : undefined;

      createAndInsertTag({
        editor,
        tagType: selectedTag.id,
        tagData: {
          taxonomyId: taxonomyId ?? '',
          documentSuperId: pDocument.superId,
          sectionSuperId: section.superId,
          pageId: activeSection.uuid,
          onId: selectionElementId,
          conceptId: conceptData?.id ?? '',
          lang: locale,
          type: getTagType(selectedTag.conceptType.baseXsdType ?? selectedTag.conceptType.baseXbrliType),
          value: selectedText || undefined,
          linkroleUri: selectedLinkroleUri ?? null,
        },
        continuationTag: continuationSource,
        successorTag: successor || null,
        callbacks: {},
      });
      clearBlockSelection?.();
    },
    [
      selectedTag,
      selectedLinkroleUri,
      editor,
      taxonomyId,
      pDocument,
      section,
      activeSection,
      selectionElementId,
      conceptData,
      locale,
      createAndInsertTag,
      clearBlockSelection,
    ]
  );

  const spansMultipleCarriers = useCallback(
    (textCarriersOnly: boolean) => {
      const range = editor.lastSelection ?? editor.selection;
      const conceptTagIds = textCarriersOnly ? new Set(existingTagsWithSameConcept.map((t) => t.id)) : undefined;
      return !!range && collectChainCarriers(editor, range, { textCarriersOnly, conceptTagIds }).length >= 2;
    },
    [editor, existingTagsWithSameConcept]
  );

  const handleTextTagging = () => {
    if (!selectedTag) return;

    if (isListItemTagging) {
      if (carrierHoldsConcept) return;
      const range = editor.selection ?? editor.lastSelection;
      const items = range ? listItemsRange(editor, range) : null;
      if (items) Transforms.select(editor, items);
    } else if (isEscapedConceptType(selectedTag.conceptType) && spansMultipleCarriers(true)) {
      tagCarrierChain('text');
      return;
    }

    if (conceptExistsInDocument) {
      setShowContinuationModal(true);
      return;
    }

    createNormalTag();
  };

  const blockCarrier = useMemo((): BlockCarrier | null => {
    const fromSelection = selectionBlockCarrier(blockSelection as CarrierBlockSelection | null, effectiveScope);
    if (fromSelection) return fromSelection;
    if (isTableBlockTagging && tableSelection?.element) {
      const el = tableSelection.element;
      if (!el.uuid) return null;
      try {
        return { path: ReactEditor.findPath(editor, el), elementId: el.uuid };
      } catch {
        return null;
      }
    }
    return null;
  }, [blockSelection, effectiveScope, isTableBlockTagging, tableSelection, editor]);

  const carrierHoldsConcept = useMemo(() => {
    const path = blockCarrier?.path ?? (isListItemTagging ? (blockSelection as CarrierBlockSelection).path : null);
    return (
      !!path &&
      carrierHoldsAnyTag(
        editor,
        path,
        existingTagsWithSameConcept.map((t) => t.id)
      )
    );
  }, [blockCarrier, isListItemTagging, blockSelection, existingTagsWithSameConcept, editor]);

  const handleExcludeContent = useCallback(() => {
    insertExclude(editor);
  }, [editor]);

  // Mark the selected report text as a footnote: wrap in Slate, persist the row.
  const handleCreateFootnote = useCallback(() => {
    const footnoteId = uuidv4();
    insertFootnote(editor, footnoteId);
    createFootnote({ id: footnoteId, documentSuperId: pDocument.superId });
  }, [editor, createFootnote, pDocument]);

  // Unplaced hidden fact: no document location — the value is entered in Tagginfo
  // and the fact emits inside <ix:hidden>.
  const handleHiddenTagging = useCallback(
    (onCreated?: (tag: unknown) => void) => {
      if (!selectedTag || !conceptData?.id) return;
      createTag(
        {
          taxonomyId: taxonomyId ?? '',
          documentSuperId: pDocument.superId,
          conceptId: conceptData.id,
          lang: locale,
          type: isFractionConceptType(selectedTag.conceptType)
            ? 'fraction'
            : getTagType(selectedTag.conceptType?.baseXsdType ?? selectedTag.conceptType?.baseXbrliType),
          hidden: true,
          linkroleUri: selectedLinkroleUri ?? null,
        },
        {
          onSuccess: (created) => onCreated?.(created),
        }
      );
    },
    [selectedTag, selectedLinkroleUri, conceptData, createTag, taxonomyId, pDocument, locale]
  );

  const createNormalBlockTag = useCallback(() => {
    if (!selectedTag || !conceptData?.id || !blockCarrier) return;
    const { path, elementId } = blockCarrier;
    createTag(
      {
        taxonomyId: taxonomyId ?? '',
        documentSuperId: pDocument.superId,
        sectionSuperId: section.superId,
        pageId: activeSection.uuid,
        onId: elementId,
        conceptId: conceptData.id,
        lang: locale,
        type: 'nonNumeric',
        linkroleUri: selectedLinkroleUri ?? null,
      },
      {
        onSuccess: (created) => {
          if (created?.id) applyBlockTagBinding(editor, path, { tagId: created.id });
          if (isImageBlockTagging || isProseBlockTagging) clearBlockSelection?.();
        },
      }
    );
  }, [
    selectedTag,
    selectedLinkroleUri,
    conceptData,
    blockCarrier,
    createTag,
    taxonomyId,
    pDocument,
    section,
    activeSection,
    locale,
    editor,
    isImageBlockTagging,
    isProseBlockTagging,
    clearBlockSelection,
  ]);

  const createBlockContinuationTag = useCallback(
    (predecessor: { tagId: string } | null, successor: { tagId: string } | null) => {
      if (!selectedTag || !conceptData?.id || !blockCarrier) return;
      if (!predecessor && !successor) return;
      const { path, elementId } = blockCarrier;
      continueTag(
        {
          taxonomyId: taxonomyId ?? '',
          documentSuperId: pDocument.superId,
          sectionSuperId: section.superId,
          pageId: activeSection.uuid,
          onId: elementId,
          conceptId: conceptData.id,
          lang: locale,
          type: 'nonNumeric',
          linkroleUri: selectedLinkroleUri ?? null,
          predecessorId: predecessor?.tagId,
          successorId: successor?.tagId,
        },
        {
          onSuccess: (created) => {
            if (created?.id) {
              applyBlockTagBinding(editor, path, {
                tagId: created.id,
                continuedAt: successor?.tagId,
              });
            }
            if (isImageBlockTagging || isProseBlockTagging) clearBlockSelection?.();
          },
        }
      );
    },
    [
      selectedTag,
      selectedLinkroleUri,
      conceptData,
      blockCarrier,
      continueTag,
      taxonomyId,
      pDocument,
      section,
      activeSection,
      locale,
      editor,
      isImageBlockTagging,
      isProseBlockTagging,
      clearBlockSelection,
    ]
  );

  const runBlockChain = useCallback(
    (predecessorId?: string, successorId?: string, textCarriersOnly = false) => {
      if (!conceptData?.id) return;
      const range = editor.lastSelection ?? editor.selection;
      if (!range) return;
      void applyBlockChain({
        editor,
        range,
        base: {
          documentSuperId: pDocument.superId,
          taxonomyId: taxonomyId ?? '',
          sectionSuperId: section.superId,
          pageId: activeSection.uuid,
          conceptId: conceptData.id,
          lang: locale,
          linkroleUri: selectedLinkroleUri ?? null,
        },
        conceptTagIds: new Set(existingTagsWithSameConcept.map((t) => t.id)),
        predecessorId,
        successorId,
        textCarriersOnly,
      }).then(
        () => clearBlockSelection?.(),
        (error) => console.error('Failed to apply block chain', error)
      );
    },
    [
      applyBlockChain,
      conceptData,
      editor,
      pDocument,
      section,
      activeSection,
      locale,
      taxonomyId,
      selectedLinkroleUri,
      existingTagsWithSameConcept,
      clearBlockSelection,
    ]
  );

  const tagCarrierChain = useCallback(
    (origin: 'text' | 'block') => {
      if (conceptExistsInDocument) {
        setPendingChainMulti(origin);
        setShowContinuationModal(true);
        return;
      }
      runBlockChain(undefined, undefined, origin === 'text');
    },
    [conceptExistsInDocument, runBlockChain]
  );

  const handleBlockTagging = useCallback(() => {
    if (!selectedTag || !conceptData?.id) return;
    if (isProseBlockTagging && spansMultipleCarriers(false)) {
      tagCarrierChain('block');
      return;
    }
    // Single carrier / image / table (unchanged).
    if (!blockCarrier || carrierHoldsConcept) return;
    if (conceptExistsInDocument) {
      setPendingBlockChain(true);
      setShowContinuationModal(true);
      return;
    }
    createNormalBlockTag();
  }, [
    selectedTag,
    conceptData,
    blockCarrier,
    carrierHoldsConcept,
    conceptExistsInDocument,
    createNormalBlockTag,
    isProseBlockTagging,
    spansMultipleCarriers,
    tagCarrierChain,
  ]);

  const handleContinuationConfirm = useCallback(() => {
    setShowContinuationModal(false);

    if (!selectedTag || existingTagsWithSameConcept.length === 0) {
      setPendingBlockChain(false);
      return;
    }

    const sections = pDocument?.content?.sections ?? [];
    const { predecessor, successor } = findChainInsertionPoint(existingTagsWithSameConcept, editor, section.superId, sections);

    if (pendingChainMulti) {
      setPendingChainMulti(false);
      runBlockChain(predecessor?.id, successor?.id, pendingChainMulti === 'text');
      return;
    }

    if (pendingBlockChain) {
      setPendingBlockChain(false);
      if (predecessor) {
        createBlockContinuationTag({ tagId: predecessor.id }, successor ? { tagId: successor.id } : null);
      } else if (successor) {
        createBlockContinuationTag(null, { tagId: successor.id });
      }
      return;
    }

    if (predecessor) {
      createContinuationTag({ tagId: predecessor.id, tagType: predecessor.concept?.name ?? selectedTag.id }, successor ? { tagId: successor.id } : null);
    } else if (successor) {
      createContinuationTag(null, { tagId: successor.id });
    }
  }, [
    selectedTag,
    existingTagsWithSameConcept,
    editor,
    pDocument,
    section,
    createContinuationTag,
    pendingBlockChain,
    createBlockContinuationTag,
    pendingChainMulti,
    runBlockChain,
  ]);

  const handleContinuationCancel = useCallback(() => {
    setShowContinuationModal(false);
    setPendingBlockChain(false);
    setPendingChainMulti(false);
  }, []);

  const handleTagging = isBlockTagging ? handleBlockTagging : isTableTagging ? handleTableTagging : handleTextTagging;

  return {
    handleTagging,
    handleHiddenTagging,
    handleExcludeContent,
    handleCreateFootnote,
    isTableTagging,
    isBlockTagging,
    hasContentMarked,
    carrierHoldsConcept,
    showContinuationModal,
    handleContinuationConfirm,
    handleContinuationCancel,
  };
};
